// src/pages/Register.js
import React, { useState, useMemo } from 'react';
import { Mail, IdCard, Lock, Eye, EyeOff, UserPlus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../Config';

const passwordRegex =
  /^(?=.*[A-Z])(?=.*\d)(?=.*[ !"#$%&'()*+,\-./:;<=>?@[\\\]^_`{|}~]).{8,}$/;

export default function Register() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    email: '',
    ci: '',
    password: '',
    confirm: '',
  });
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [serverError, setServerError] = useState('');

  const errors = useMemo(() => {
    const e = {};
    if (!form.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = 'Ingresa un email válido.';
    if (!form.ci || !/^\d{5,}$/.test(form.ci))
      e.ci = 'Cédula inválida (solo números, mínimo 5 dígitos).';
    if (!form.password || !passwordRegex.test(form.password))
      e.password =
        'Mín. 8 caract., al menos 1 mayúscula, 1 número y 1 caracter especial.';
    if (!form.confirm || form.confirm !== form.password)
      e.confirm = 'Las contraseñas no coinciden.';
    return e;
  }, [form]);

  const isFormValid = Object.keys(errors).length === 0;

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Para CI, permitimos solo números
    setForm((f) => ({
      ...f,
      [name]: name === 'ci' ? value.replace(/\D/g, '') : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError('');
    if (!isFormValid) return;

    try {
      setIsLoading(true);
      const res = await fetch(`${API_BASE_URL}/usuarios`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          // Claves alineadas al backend:
          correo: form.email,       // @JsonAlias({"email","correo"}) -> acepta ambas
          cedula: form.ci,
          contrasena: form.password,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || `Error ${res.status}`);
      }

      // Registro OK → a login
      navigate('/login', { replace: true });
    } catch (err) {
      setServerError(err.message || 'Error al registrar.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-lg p-6">
        <div className="flex items-center gap-2 mb-6">
          <UserPlus className="h-6 w-6" />
          <h1 className="text-xl font-semibold">Crear cuenta</h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Email */}
          <div>
            <label className="block text-sm font-medium mb-1">Email</label>
            <div className={`flex items-center gap-2 border rounded-lg px-3 py-2 ${errors.email ? 'border-red-500' : 'border-slate-300'}`}>
              <Mail className="h-5 w-5 opacity-70" />
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                placeholder="tu@correo.com"
                className="w-full outline-none bg-transparent"
                autoComplete="email"
              />
            </div>
            {errors.email && <p className="text-xs text-red-600 mt-1">{errors.email}</p>}
          </div>

          {/* Cédula */}
          <div>
            <label className="block text-sm font-medium mb-1">Número de cédula</label>
            <div className={`flex items-center gap-2 border rounded-lg px-3 py-2 ${errors.ci ? 'border-red-500' : 'border-slate-300'}`}>
              <IdCard className="h-5 w-5 opacity-70" />
              <input
                type="text"
                name="ci"
                value={form.ci}
                onChange={handleChange}
                placeholder="Ej: 4790123"
                className="w-full outline-none bg-transparent"
                inputMode="numeric"
              />
            </div>
            {errors.ci && <p className="text-xs text-red-600 mt-1">{errors.ci}</p>}
          </div>

          {/* Contraseña */}
          <div>
            <label className="block text-sm font-medium mb-1">Contraseña</label>
            <div className={`flex items-center gap-2 border rounded-lg px-3 py-2 ${errors.password ? 'border-red-500' : 'border-slate-300'}`}>
              <Lock className="h-5 w-5 opacity-70" />
              <input
                type={showPwd ? 'text' : 'password'}
                name="password"
                value={form.password}
                onChange={handleChange}
                placeholder="••••••••"
                className="w-full outline-none bg-transparent"
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowPwd((s) => !s)}
                className="p-1 rounded hover:bg-slate-100"
                aria-label={showPwd ? 'Ocultar contraseña' : 'Ver contraseña'}
              >
                {showPwd ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
            {errors.password && <p className="text-xs text-red-600 mt-1">{errors.password}</p>}
            <p className="text-xs text-slate-500 mt-1">
              Requisitos: 8+ caract., 1 mayúscula, 1 número y 1 especial.
            </p>
          </div>

          {/* Confirmar contraseña */}
          <div>
            <label className="block text-sm font-medium mb-1">Confirmar contraseña</label>
            <div className={`flex items-center gap-2 border rounded-lg px-3 py-2 ${errors.confirm ? 'border-red-500' : 'border-slate-300'}`}>
              <Lock className="h-5 w-5 opacity-70" />
              <input
                type={showConfirm ? 'text' : 'password'}
                name="confirm"
                value={form.confirm}
                onChange={handleChange}
                placeholder="••••••••"
                className="w-full outline-none bg-transparent"
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowConfirm((s) => !s)}
                className="p-1 rounded hover:bg-slate-100"
                aria-label={showConfirm ? 'Ocultar contraseña' : 'Ver contraseña'}
              >
                {showConfirm ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
            {errors.confirm && <p className="text-xs text-red-600 mt-1">{errors.confirm}</p>}
          </div>

          {serverError && (
            <div className="text-sm text-red-600">{serverError}</div>
          )}

          <button
            type="submit"
            disabled={!isFormValid || isLoading}
            className="w-full bg-cyan-600 text-white py-3 px-4 rounded-lg hover:bg-cyan-700 focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            ) : (
              <>
                <UserPlus className="h-5 w-5" />
                Crear cuenta
              </>
            )}
          </button>

          <button
            type="button"
            onClick={() => navigate('/login')}
            className="w-full py-3 px-4 rounded-lg border border-slate-300 hover:bg-slate-50 transition-colors"
          >
            ¿Ya tienes cuenta? Inicia sesión
          </button>
        </form>
      </div>
    </div>
  );
}
